import { z } from "zod";
import {
  dataStateSchema,
  freshnessSchema,
  qualitySchema,
  reconciliationSchema,
} from "./governance";
import {
  isoDateTimeSchema,
  opaqueIdSchema,
  permissionKeySchema,
  versionSchema,
} from "./primitives";

export const dataConfidenceMetricIdSchema = z.enum(["K073", "K074", "K075", "K076"]);

export const dataConfidenceProvenanceSchema = z
  .object({
    sourceConnectionId: opaqueIdSchema,
    ingestionRunId: opaqueIdSchema,
    sourceIdentifier: z.string().trim().min(1).max(500),
    sourceName: z.string().trim().min(1).max(300),
    authorityClass: z.string().trim().min(1).max(100),
  })
  .strict();

export const dataConfidenceMetricSchema = z
  .object({
    metricId: dataConfidenceMetricIdSchema,
    definitionVersion: versionSchema,
    definitionStatus: z.enum(["DRAFT", "REVIEW", "APPROVED", "ACTIVE", "RETIRED", "UNAVAILABLE"]),
    formulaAuthority: z.string().trim().min(1).max(100),
    label: z.string().trim().min(1).max(200),
    status: z.enum(["AVAILABLE", "UNAVAILABLE"]),
    value: z.union([z.number().finite(), z.string().trim().min(1), z.null()]),
    unit: z.string().trim().min(1).max(50).optional(),
    displayValue: z.string().trim().min(1).max(200),
    calculationRunId: opaqueIdSchema.optional(),
    dataCutoffAt: isoDateTimeSchema.optional(),
    dataState: dataStateSchema,
    authorityClass: z.string().trim().min(1).max(100),
    productionUseProhibited: z.boolean(),
    sourceSummary: z.string().trim().min(1).max(1_000),
    freshness: freshnessSchema,
    quality: qualitySchema,
    reconciliation: reconciliationSchema,
    permissions: z.array(permissionKeySchema).min(1).max(20),
    provenance: z.array(dataConfidenceProvenanceSchema).max(100),
    unavailableReason: z.string().trim().min(1).max(1_000).optional(),
  })
  .strict()
  .superRefine((metric, context) => {
    if (metric.status === "UNAVAILABLE") {
      if (
        metric.value !== null ||
        metric.calculationRunId !== undefined ||
        metric.dataCutoffAt !== undefined ||
        metric.provenance.length > 0 ||
        metric.dataState !== "UNAVAILABLE"
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["status"],
          message: "Unavailable Data Confidence metrics cannot carry governed values or lineage.",
        });
      }
      if (!metric.unavailableReason) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["unavailableReason"],
          message: "Unavailable Data Confidence metrics require an explicit reason.",
        });
      }
      return;
    }

    if (
      metric.value === null ||
      !metric.calculationRunId ||
      !metric.dataCutoffAt ||
      metric.provenance.length === 0 ||
      metric.unavailableReason
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["status"],
        message: "Available Data Confidence metrics require a value, cutoff, run and provenance.",
      });
    }

    if (metric.dataState === "SYNTHETIC") {
      if (metric.authorityClass !== "DEMONSTRATION_ONLY" || !metric.productionUseProhibited) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["authorityClass"],
          message:
            "Synthetic Data Confidence values must remain demonstration-only and production-prohibited.",
        });
      }
    } else {
      if (metric.productionUseProhibited) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["productionUseProhibited"],
          message: "Only synthetic Data Confidence values may be production-prohibited.",
        });
      }
      if (!["APPROVED", "ACTIVE"].includes(metric.definitionStatus)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["definitionStatus"],
          message: "Non-synthetic values require an approved or active metric definition.",
        });
      }
    }
  });

export const dataConfidenceResponseSchema = z
  .object({
    view: z.literal("data-confidence"),
    schemaVersion: versionSchema,
    tenantId: opaqueIdSchema,
    generatedAt: isoDateTimeSchema,
    dataAsOf: isoDateTimeSchema,
    metrics: z.array(dataConfidenceMetricSchema).length(4),
    warnings: z
      .array(
        z
          .object({
            code: z
              .string()
              .trim()
              .regex(/^[A-Z][A-Z0-9_]*$/)
              .max(100),
            message: z.string().trim().min(1).max(1_000),
            metricIds: z.array(dataConfidenceMetricIdSchema).max(4).optional(),
          })
          .strict(),
      )
      .max(20),
  })
  .strict()
  .superRefine((response, context) => {
    const ids = response.metrics.map(({ metricId }) => metricId);
    const expected = ["K073", "K074", "K075", "K076"];
    if (
      new Set(ids).size !== 4 ||
      expected.some((id) => !ids.includes(id as (typeof ids)[number]))
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["metrics"],
        message: "Data Confidence response must contain K073 through K076 exactly once.",
      });
    }
  });

export type DataConfidenceMetricId = z.infer<typeof dataConfidenceMetricIdSchema>;
export type DataConfidenceProvenance = z.infer<typeof dataConfidenceProvenanceSchema>;
export type DataConfidenceMetric = z.infer<typeof dataConfidenceMetricSchema>;
export type DataConfidenceResponse = z.infer<typeof dataConfidenceResponseSchema>;
